import { toast, Toaster } from 'sonner';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Loader2,
  ShieldAlert,
  BellRing,
} from 'lucide-react';
import React from 'react';

/**
 * Enterprise-grade toast notification system
 * Features premium styling with glass morphism, subtle animations,
 * and professional visual hierarchy
 */

// Custom toast container configuration
export const ToasterConfig = () => {
  const isDark = document.documentElement.classList.contains('dark');

  return (
    <Toaster
      position="top-right"
      expand={true}
      richColors={false}
      closeButton={true}
      duration={5000}
      gap={12}
      toastOptions={{
        style: {
          background: isDark
            ? 'rgba(17, 24, 39, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: isDark
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          padding: '16px 20px',
          boxShadow: isDark
            ? `
              0 4px 6px -1px rgba(0, 0, 0, 0.3),
              0 2px 4px -1px rgba(0, 0, 0, 0.2),
              0 20px 25px -5px rgba(0, 0, 0, 0.3),
              0 10px 10px -5px rgba(0, 0, 0, 0.2)
            `
            : `
              0 4px 6px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06),
              0 20px 25px -5px rgba(0, 0, 0, 0.1),
              0 10px 10px -5px rgba(0, 0, 0, 0.04)
            `,
          minWidth: '380px',
          fontFamily:
            'Tajawal, tajwool, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: '14px',
          lineHeight: '1.5',
          color: isDark ? '#f9fafb' : '#111827',
        },
        className: 'enterprise-toast',
        duration: 5000,
      }}
    />
  );
};

// Base toast styles for consistency
const baseToastStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
};

const iconContainerStyle = (color: string, bgColor: string) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  backgroundColor: bgColor,
  flexShrink: 0,
  position: 'relative' as const,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, transparent, ${color}20)`,
    opacity: 0.5,
  },
});

const contentStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '4px',
  paddingTop: '2px',
};

const titleStyle = (color: string) => {
  const isDark = document.documentElement.classList.contains('dark');
  return {
    fontSize: '15px',
    fontWeight: 600,
    color: isDark ? '#f9fafb' : color,
    letterSpacing: '-0.025em',
    marginBottom: '2px',
  };
};

const descriptionStyle = {
  fontSize: '13px',
  color: document.documentElement.classList.contains('dark')
    ? '#d1d5db'
    : '#64748b',
  lineHeight: '1.5',
  letterSpacing: '-0.01em',
};

// Progress bar component for loading states
const ProgressBar = ({ color }: { color: string }) => (
  <div
    style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: 'rgba(0, 0, 0, 0.05)',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        height: '100%',
        width: '30%',
        background: color,
        animation: 'shimmer 1.5s ease-in-out infinite',
      }}
    />
  </div>
);

// Success toast with professional styling
export const showSuccess = (
  title: string,
  description?: string,
  options?: {
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
    icon?: React.ReactNode;
  },
) => {
  return toast.custom(
    () => (
      <div style={baseToastStyle}>
        <div style={iconContainerStyle('#10b981', '#10b98110')}>
          {options?.icon || (
            <CheckCircle2 size={22} color="#10b981" strokeWidth={2.5} />
          )}
        </div>
        <div style={contentStyle}>
          <div style={titleStyle('#0f172a')}>{title}</div>
          {description && <div style={descriptionStyle}>{description}</div>}
        </div>
        {options?.action && (
          <button
            onClick={options.action.onClick}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#10b981',
              background: '#10b98110',
              border: '1px solid #10b98120',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              letterSpacing: '-0.01em',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#10b98120';
              e.currentTarget.style.borderColor = '#10b98130';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#10b98110';
              e.currentTarget.style.borderColor = '#10b98120';
            }}
          >
            {options.action.label}
          </button>
        )}
      </div>
    ),
    { duration: options?.duration || 4000 },
  );
};

// Error toast with attention-grabbing design
export const showError = (
  title: string,
  description?: string,
  options?: {
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
    icon?: React.ReactNode;
  },
) => {
  return toast.custom(
    () => (
      <div style={baseToastStyle}>
        <div style={iconContainerStyle('#ef4444', '#ef444410')}>
          {options?.icon || (
            <XCircle size={22} color="#ef4444" strokeWidth={2.5} />
          )}
        </div>
        <div style={contentStyle}>
          <div style={titleStyle('#0f172a')}>{title}</div>
          {description && <div style={descriptionStyle}>{description}</div>}
        </div>
        {options?.action && (
          <button
            onClick={options.action.onClick}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#ef4444',
              background: '#ef444410',
              border: '1px solid #ef444420',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              letterSpacing: '-0.01em',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ef444420';
              e.currentTarget.style.borderColor = '#ef444430';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#ef444410';
              e.currentTarget.style.borderColor = '#ef444420';
            }}
          >
            {options.action.label}
          </button>
        )}
      </div>
    ),
    { duration: options?.duration || 6000 },
  );
};

// Warning toast with caution styling
export const showWarning = (
  title: string,
  description?: string,
  options?: {
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
    icon?: React.ReactNode;
  },
) => {
  return toast.custom(
    () => (
      <div style={baseToastStyle}>
        <div style={iconContainerStyle('#f59e0b', '#f59e0b10')}>
          {options?.icon || (
            <AlertTriangle size={22} color="#f59e0b" strokeWidth={2.5} />
          )}
        </div>
        <div style={contentStyle}>
          <div style={titleStyle('#0f172a')}>{title}</div>
          {description && <div style={descriptionStyle}>{description}</div>}
        </div>
        {options?.action && (
          <button
            onClick={options.action.onClick}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#f59e0b',
              background: '#f59e0b10',
              border: '1px solid #f59e0b20',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              letterSpacing: '-0.01em',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f59e0b20';
              e.currentTarget.style.borderColor = '#f59e0b30';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f59e0b10';
              e.currentTarget.style.borderColor = '#f59e0b20';
            }}
          >
            {options.action.label}
          </button>
        )}
      </div>
    ),
    { duration: options?.duration || 5000 },
  );
};

// Info toast with informative styling
export const showInfo = (
  title: string,
  description?: string,
  options?: {
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
    icon?: React.ReactNode;
  },
) => {
  return toast.custom(
    () => (
      <div style={baseToastStyle}>
        <div style={iconContainerStyle('#3b82f6', '#3b82f610')}>
          {options?.icon || (
            <Info size={22} color="#3b82f6" strokeWidth={2.5} />
          )}
        </div>
        <div style={contentStyle}>
          <div style={titleStyle('#0f172a')}>{title}</div>
          {description && <div style={descriptionStyle}>{description}</div>}
        </div>
        {options?.action && (
          <button
            onClick={options.action.onClick}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#3b82f6',
              background: '#3b82f610',
              border: '1px solid #3b82f620',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              letterSpacing: '-0.01em',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#3b82f620';
              e.currentTarget.style.borderColor = '#3b82f630';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#3b82f610';
              e.currentTarget.style.borderColor = '#3b82f620';
            }}
          >
            {options.action.label}
          </button>
        )}
      </div>
    ),
    { duration: options?.duration || 4000 },
  );
};

// Loading toast with animated progress
export const showLoading = (title: string, description?: string) => {
  return toast.custom(
    () => (
      <div style={{ ...baseToastStyle, position: 'relative' }}>
        <div style={iconContainerStyle('#6366f1', '#6366f110')}>
          <Loader2
            size={22}
            color="#6366f1"
            strokeWidth={2.5}
            className="animate-spin"
          />
        </div>
        <div style={contentStyle}>
          <div style={titleStyle('#0f172a')}>{title}</div>
          {description && <div style={descriptionStyle}>{description}</div>}
        </div>
        <ProgressBar color="#6366f1" />
      </div>
    ),
    { duration: Infinity },
  );
};

// Security/permission toast variants
export const showSecurityAlert = (
  title: string,
  description?: string,
  severity: 'high' | 'medium' | 'low' = 'medium',
) => {
  const colors = {
    high: { primary: '#dc2626', bg: '#dc262610' },
    medium: { primary: '#ea580c', bg: '#ea580c10' },
    low: { primary: '#ca8a04', bg: '#ca8a0410' },
  };

  const color = colors[severity];

  return toast.custom(
    () => (
      <div style={baseToastStyle}>
        <div style={iconContainerStyle(color.primary, color.bg)}>
          <ShieldAlert size={22} color={color.primary} strokeWidth={2.5} />
        </div>
        <div style={contentStyle}>
          <div style={titleStyle('#0f172a')}>{title}</div>
          {description && <div style={descriptionStyle}>{description}</div>}
        </div>
      </div>
    ),
    { duration: 8000 },
  );
};

// Notification toast for general updates
export const showNotification = (
  title: string,
  description?: string,
  options?: {
    duration?: number;
    icon?: React.ReactNode;
  },
) => {
  return toast.custom(
    () => (
      <div style={baseToastStyle}>
        <div style={iconContainerStyle('#8b5cf6', '#8b5cf610')}>
          {options?.icon || (
            <BellRing size={22} color="#8b5cf6" strokeWidth={2.5} />
          )}
        </div>
        <div style={contentStyle}>
          <div style={titleStyle('#0f172a')}>{title}</div>
          {description && <div style={descriptionStyle}>{description}</div>}
        </div>
      </div>
    ),
    { duration: options?.duration || 4000 },
  );
};

// Promise toast with enhanced styling
export const showPromise = <T,>(
  promise: Promise<T>,
  {
    loading,
    success,
    error,
  }: {
    loading: { title: string; description?: string };
    success: { title: string; description?: string };
    error: { title: string; description?: string };
  },
) => {
  const toastId = showLoading(loading.title, loading.description);

  promise
    .then(() => {
      dismissToast(toastId);
      showSuccess(success.title, success.description);
    })
    .catch(() => {
      dismissToast(toastId);
      showError(error.title, error.description);
    });

  return toastId;
};

// Dismiss functions
export const dismissToast = (toastId: string | number) => {
  toast.dismiss(toastId);
};

export const dismissAllToasts = () => {
  toast.dismiss();
};

// Arabic variants with RTL support and Tajawal font
const arabicStyle = {
  fontFamily: 'Tajawal, tajwool, -apple-system, BlinkMacSystemFont, sans-serif',
  direction: 'rtl' as const,
};

export const showSuccessAr = (
  title: string,
  description?: string,
  options?: {
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
  },
) => {
  return toast.custom(
    () => (
      <div style={{ ...baseToastStyle, ...arabicStyle }}>
        <div style={iconContainerStyle('#10b981', '#10b98110')}>
          <CheckCircle2 size={22} color="#10b981" strokeWidth={2.5} />
        </div>
        <div style={contentStyle}>
          <div style={{ ...titleStyle('#0f172a'), ...arabicStyle }}>
            {title}
          </div>
          {description && (
            <div style={{ ...descriptionStyle, ...arabicStyle }}>
              {description}
            </div>
          )}
        </div>
      </div>
    ),
    { duration: options?.duration || 4000 },
  );
};

export const showErrorAr = (
  title: string,
  description?: string,
  options?: {
    duration?: number;
  },
) => {
  return toast.custom(
    () => (
      <div style={{ ...baseToastStyle, ...arabicStyle }}>
        <div style={iconContainerStyle('#ef4444', '#ef444410')}>
          <XCircle size={22} color="#ef4444" strokeWidth={2.5} />
        </div>
        <div style={contentStyle}>
          <div style={{ ...titleStyle('#0f172a'), ...arabicStyle }}>
            {title}
          </div>
          {description && (
            <div style={{ ...descriptionStyle, ...arabicStyle }}>
              {description}
            </div>
          )}
        </div>
      </div>
    ),
    { duration: options?.duration || 6000 },
  );
};

export const showWarningAr = (
  title: string,
  description?: string,
  options?: {
    duration?: number;
  },
) => {
  return toast.custom(
    () => (
      <div style={{ ...baseToastStyle, ...arabicStyle }}>
        <div style={iconContainerStyle('#f59e0b', '#f59e0b10')}>
          <AlertTriangle size={22} color="#f59e0b" strokeWidth={2.5} />
        </div>
        <div style={contentStyle}>
          <div style={{ ...titleStyle('#0f172a'), ...arabicStyle }}>
            {title}
          </div>
          {description && (
            <div style={{ ...descriptionStyle, ...arabicStyle }}>
              {description}
            </div>
          )}
        </div>
      </div>
    ),
    { duration: options?.duration || 5000 },
  );
};

export const showInfoAr = (
  title: string,
  description?: string,
  options?: {
    duration?: number;
  },
) => {
  return toast.custom(
    () => (
      <div style={{ ...baseToastStyle, ...arabicStyle }}>
        <div style={iconContainerStyle('#3b82f6', '#3b82f610')}>
          <Info size={22} color="#3b82f6" strokeWidth={2.5} />
        </div>
        <div style={contentStyle}>
          <div style={{ ...titleStyle('#0f172a'), ...arabicStyle }}>
            {title}
          </div>
          {description && (
            <div style={{ ...descriptionStyle, ...arabicStyle }}>
              {description}
            </div>
          )}
        </div>
      </div>
    ),
    { duration: options?.duration || 4000 },
  );
};

// CSS animation for progress bar
const styles = `
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(400%);
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .enterprise-toast {
    animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

// Export base toast for advanced usage
export { toast };
